// Doc 08 §4, §8 — /join/:token page. Reachable signed OUT as well as signed in
// (the route exists in both router stacks — a join link is most often opened by
// someone without a session).
//
// Flow:
//   • Signed in  → validate the token up front via viewViaLink; revoked/expired/
//     missing tokens show the invalid state immediately.
//   • Signed out → show the invite landing; "View" signs the visitor in
//     ANONYMOUSLY first (Doc 08 §4a: rules need a uid to hold the read grant, but
//     no sign-up form should stand between a link and viewing), then viewViaLink,
//     then lands on the read-only resource.
//   • "Join" — a real account calls joinViaLink and navigates into the resource.
//     A signed-out or anonymous visitor gets two ways in (T31): "Join as guest",
//     which asks for a display name and joins the anonymous session as a
//     link-scoped guest member (full rights of the link's role, gone when the link
//     is revoked), or sign-up, which carries the token in the query so the claim
//     completes after account creation (the anonymous-upgrade path).

import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, vars } from "@pack-it-up/ui-web";
import { isOk } from "@pack-it-up/shared";
import type { ResourceRef } from "@pack-it-up/core";
import { useStores } from "../../app/AppContext.js";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { useSessionDeps } from "../../app/sessionDeps.js";

type PageState =
  | { kind: "loading" }
  // Signed-out landing: the token hasn't been validated yet (validation needs a uid
  // on the real backend) — View/Join both start their flows from here.
  | { kind: "landing" }
  // `allowJoin: false` = view-only link → no Join affordance.
  | { kind: "preview"; resource: ResourceRef; allowJoin: boolean }
  | { kind: "invalid"; reason: string }
  | { kind: "joining" }
  | { kind: "joined"; resource: ResourceRef };

// ── Layout helpers ────────────────────────────────────────────────────────────

const pageStyle = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: vars.color.background,
  padding: vars.space.lg,
};

const cardStyle = {
  maxWidth: "440px",
  width: "100%",
  background: vars.color.surface,
  border: `1px solid ${vars.color.border}`,
  borderRadius: vars.radius.lg,
  padding: vars.space.xl,
  display: "flex",
  flexDirection: "column" as const,
  gap: vars.space.md,
};

const headingStyle = {
  fontFamily: vars.font.heading,
  fontWeight: vars.font.weightBold,
  fontSize: vars.font.sizeLg,
  color: vars.color.text,
  margin: 0,
};

const bodyTextStyle = {
  fontSize: vars.font.sizeSm,
  color: vars.color.textMuted,
};

const actionRowStyle = {
  display: "flex",
  gap: vars.space.sm,
  marginTop: vars.space.sm,
};

const bannerStyle = {
  background: vars.color.accentSubtle,
  borderRadius: vars.radius.sm,
  padding: `${vars.space.sm} ${vars.space.md}`,
  fontSize: vars.font.sizeSm,
  color: vars.color.accent,
  fontWeight: vars.font.weightMedium,
};

const guestInputStyle = {
  background: vars.color.background,
  border: `1px solid ${vars.color.border}`,
  borderRadius: vars.radius.sm,
  padding: `${vars.space.sm} ${vars.space.md}`,
  color: vars.color.text,
  fontFamily: "inherit",
  fontSize: vars.font.sizeSm,
  outline: "none",
};

const linkButtonStyle = {
  background: "none",
  border: "none",
  padding: 0,
  color: vars.color.textMuted,
  fontFamily: "inherit",
  fontSize: vars.font.sizeXs,
  textDecoration: "underline",
  textUnderlineOffset: "2px",
  cursor: "pointer",
  alignSelf: "flex-start" as const,
};

const INVALID_REASON ="This invite link is invalid, revoked, or has expired.";

// ── Page ──────────────────────────────────────────────────────────────────────

export const JoinPage = (): ReactNode => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { auth } = useStores();
  const { repos } = useSessionDeps();

  const user = useStoreValue(auth, (s) => s.user);
  const authStatus = useStoreValue(auth, (s) => s.status);

  const [state, setState] = useState<PageState>({ kind: "loading" });
  // Guest-join name prompt (T31) — shown in place of the action row once a signed-out
  // or anonymous visitor picks Join.
  const [namePrompt, setNamePrompt] = useState(false);
  const [guestName, setGuestName] = useState("");

  const navigateTo = (ref: ResourceRef): void => {
    navigate(ref.kind === "trip" ? `/trip/${ref.id}` : `/template/${ref.id}`);
  };

  // On mount: signed-in sessions validate the token up front; signed-out visitors
  // get the landing (validation happens on their first action, which mints the
  // anonymous uid the grant needs).
  useEffect(() => {
    if (token === undefined || token === "") {
      setState({ kind: "invalid", reason: "No invite token provided." });
      return;
    }
    if (authStatus === "loading") return; // wait for auth to resolve
    if (authStatus === "signedOut") {
      setState((s) => (s.kind === "loading" ? { kind: "landing" } : s));
      return;
    }

    const run = async (): Promise<void> => {
      const result = await repos.sharing.viewViaLink(token);
      if (isOk(result)) {
        setState({ kind: "preview", resource: result.value.resource, allowJoin: result.value.allowJoin });
      } else {
        setState({ kind: "invalid", reason: INVALID_REASON });
      }
    };
    void run();
  }, [token, repos.sharing, authStatus]);

  // "View without joining" (Doc 08 §4a): make sure *a* uid exists (anonymous is
  // fine — created invisibly here), redeem the view grant, land read-only.
  const handleView = async (): Promise<void> => {
    if (token === undefined) return;
    if (state.kind === "preview") {
      navigateTo(state.resource);
      return;
    }
    // Signed-out landing → anonymous-auth first.
    if (user === null) {
      const signedIn = await auth.getState().signInAnonymously();
      if (!signedIn.ok) {
        setState({ kind: "invalid", reason: "Could not start a viewing session. Try again." });
        return;
      }
    }
    const result = await repos.sharing.viewViaLink(token);
    if (isOk(result)) {
      navigateTo(result.value.resource);
    } else {
      setState({ kind: "invalid", reason: INVALID_REASON });
    }
  };

  // "Join" (Doc 08 §4b) for a real account — joins under the account's own profile.
  // Signed-out/anonymous visitors take `handleGuestJoin` or the sign-up path instead.
  const handleJoin = async (): Promise<void> => {
    if (token === undefined) return;
    if (user === null || user.isAnonymous) {
      // A guest who already named itself on an earlier link keeps that name on the auth
      // profile — offer it rather than asking twice.
      if (user?.isAnonymous === true && user.displayName !== "") setGuestName(user.displayName);
      setNamePrompt(true);
      return;
    }
    setState({ kind: "joining" });
    const result = await repos.sharing.joinViaLink(token);
    if (isOk(result)) {
      setState({ kind: "joined", resource: result.value });
      navigateTo(result.value);
    } else {
      setState({ kind: "invalid", reason: "Could not join. The link may have been revoked." });
    }
  };

  // T31 "Join as guest": the anonymous session becomes a link-scoped member carrying the
  // link's role. The typed name is the guest's only identity (there's no profile doc), so
  // it's required — the callable rejects an empty one.
  const handleGuestJoin = async (): Promise<void> => {
    if (token === undefined) return;
    const name = guestName.trim();
    if (name === "") return;
    if (user === null) {
      const signedIn = await auth.getState().signInAnonymously();
      if (!signedIn.ok) {
        setState({ kind: "invalid", reason: "Could not start a session. Try again." });
        return;
      }
    }
    setState({ kind: "joining" });
    const result = await repos.sharing.joinViaLink(token, name);
    if (isOk(result)) {
      // Remember the name on the auth profile (the only place an anonymous session can
      // keep one) so the next link this browser opens prefills it. Best-effort: the join
      // already succeeded, and a failed rename only costs a second prompt later.
      await auth.getState().setAuthDisplayName(name);
      setState({ kind: "joined", resource: result.value });
      navigateTo(result.value);
    } else {
      setState({ kind: "invalid", reason: "Could not join. The link may have been revoked." });
    }
  };

  const hasRealAccount = user !== null && !user.isAnonymous;
  // A validated view-only link never offers Join. On the signed-out landing the
  // token isn't validated yet (needs a uid), so the affordance shows until it is;
  // joinViaLink still rejects view-only links server-side.
  const canJoin = state.kind !== "preview" || state.allowJoin;

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        {state.kind === "loading" && (
          <>
            <h1 style={headingStyle}>Loading invite…</h1>
            <p style={bodyTextStyle}>Checking your invite link.</p>
          </>
        )}

        {state.kind === "invalid" && (
          <>
            <h1 style={headingStyle} data-testid="invalid-heading">
              Invalid invite
            </h1>
            <p style={bodyTextStyle}>{state.reason}</p>
            <div style={actionRowStyle}>
              <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
                Go home
              </Button>
            </div>
          </>
        )}

        {(state.kind === "landing" || state.kind === "preview") && (
          <>
            <h1 style={headingStyle}>You're invited</h1>
            <p style={bodyTextStyle}>
              {state.kind === "preview"
                ? `You have been invited to a ${state.resource.kind}. Choose how you'd like to access it.`
                : "Someone shared a trip or template with you. Choose how you'd like to access it."}
            </p>
            <div style={bannerStyle}>
              {canJoin ? "Viewing via link — Join to participate" : "View-only link"}
            </div>
            {namePrompt ? (
              <>
                <label htmlFor="guest-name" style={bodyTextStyle}>
                  What should the others call you?
                </label>
                <input
                  id="guest-name"
                  autoFocus
                  value={guestName}
                  maxLength={60}
                  placeholder="Your name"
                  onChange={(e) => setGuestName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void handleGuestJoin();
                  }}
                  style={guestInputStyle}
                />
                <div style={actionRowStyle}>
                  <Button variant="ghost" size="sm" onClick={() => setNamePrompt(false)}>
                    Back
                  </Button>
                  <Button size="sm" disabled={guestName.trim() === ""} onClick={() => { void handleGuestJoin(); }}>
                    Join as guest
                  </Button>
                </div>
                {/* Signing up instead keeps the trip on a real account — a guest lives
                    only as long as the link that admitted it. */}
                <button type="button" style={linkButtonStyle} onClick={() => navigate(`/signup?joinToken=${token ?? ""}`)}>
                  Or sign up for an account instead
                </button>
              </>
            ) : (
              <div style={actionRowStyle}>
                <Button variant="ghost" size="sm" onClick={() => { void handleView(); }}>
                  View (read-only)
                </Button>
                {canJoin && (
                  <Button size="sm" onClick={() => { void handleJoin(); }}>
                    {hasRealAccount ? "Join" : "Join as guest"}
                  </Button>
                )}
              </div>
            )}
          </>
        )}

        {state.kind === "joining" && (
          <>
            <h1 style={headingStyle}>Joining…</h1>
            <p style={bodyTextStyle}>Adding you as a member.</p>
          </>
        )}

        {state.kind === "joined" && (
          <>
            <h1 style={headingStyle}>Joined!</h1>
            <p style={bodyTextStyle}>You've been added. Redirecting…</p>
          </>
        )}
      </div>
    </div>
  );
};
